import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAcoesVisita } from "../lib/acoesVisita";
import { buscarVisitas } from "../lib/visitas";
import { Cabecalho } from "../components/Layout";
import { BotaoNovaVisita } from "../components/BotaoNovaVisita";
import { TabelaVisitas } from "../components/TabelaVisitas";
import { Funil, OrigemLeads, PrecisaDeVoce } from "../components/PainelDashboard";
import type { Kpis, Origens, Pendencias, Visita } from "../lib/tipos";

/**
 * Cartão de número. Cada um leva para a lista que explica aquele número —
 * ver "23 em andamento" sem conseguir chegar nas 23 é dado, não informação.
 */
function Cartao({
  icone, rotulo, valor, cor, para,
}: {
  icone: string; rotulo: string; valor: number; cor: string; para: string;
}) {
  return (
    <Link
      to={para}
      className="cartao"
      title={`Ver ${rotulo.toLowerCase()}`}
      style={{ flex: "1 1 180px", minWidth: 170, display: "flex", alignItems: "center", gap: 14, textDecoration: "none", color: "inherit", transition: "border-color .15s, transform .15s" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = cor; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = ""; e.currentTarget.style.transform = ""; }}
    >
      <div style={{ fontSize: 26 }}>{icone}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, color: "var(--texto-suave)" }}>{rotulo}</div>
        <div style={{ fontSize: 30, fontWeight: 700, color: cor, lineHeight: 1.1 }}>{valor}</div>
      </div>
      <div style={{ fontSize: 16, color: "var(--texto-suave)", opacity: .5 }}>›</div>
    </Link>
  );
}

/**
 * O bloco "Ligações de Confirmação Pendentes" saiu daqui.
 *
 * Ele existia no 4.x porque alguém precisava ligar para confirmar presença.
 * O lembrete automático de 3 horas passou a fazer isso, e manter o aviso na
 * tela pedia trabalho que já está feito — o pior tipo de alerta.
 */
export default function Dashboard() {
  const { podeAoMenos } = useAuth();
  const podeEditar = podeAoMenos("CONSULTOR");

  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [pendencias, setPendencias] = useState<Pendencias | null>(null);
  const [origens, setOrigens] = useState<Origens | null>(null);
  const [ultimas, setUltimas] = useState<Visita[]>([]);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    try {
      const [k, pend, orig, recentes] = await Promise.all([
        get<Kpis>("/api/dashboard/kpis"),
        get<Pendencias>("/api/dashboard/pendencias"),
        get<Origens>("/api/dashboard/origens"),
        buscarVisitas({ porPagina: 10 }),
      ]);
      setKpis(k);
      setPendencias(pend);
      setOrigens(orig);
      setUltimas(recentes.itens);
      setErro("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao carregar o dashboard");
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const acoes = useAcoesVisita(carregar);

  return (
    <>
      <Cabecalho titulo="Dashboard" />
      {podeEditar && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
          <BotaoNovaVisita onClick={acoes.abrirNova} />
        </div>
      )}

      {erro && <div className="erro" style={{ marginBottom: 16 }}>{erro}</div>}

      {kpis && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }}>
          <Cartao icone="📋" rotulo="Total de Visitas" valor={kpis.total} cor="var(--verde)" para="/visitas" />
          <Cartao icone="📅" rotulo="Visitas Hoje" valor={kpis.hoje} cor="#1570ef" para="/hoje" />
          <Cartao icone="✅" rotulo="Matrículas" valor={kpis.matriculas} cor="#079455" para="/matriculas" />
          <Cartao icone="⏳" rotulo="Em Andamento" valor={kpis.emAndamento} cor="#dc6803" para="/visitas?status=EM_ANDAMENTO" />
          <Cartao icone="❌" rotulo="Não Efetivadas" valor={kpis.perdidas} cor="#d92d20" para="/nao-efetivadas" />
        </div>
      )}

      {pendencias && (
        <PrecisaDeVoce dados={pendencias} onAbrir={acoes.abrirEdicaoPorId} aoContatar={carregar} />
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
        {kpis && <Funil kpis={kpis} />}
        {origens && <OrigemLeads dados={origens} />}
      </div>

      <h2 style={{ fontSize: 16, margin: "0 0 12px" }}>Últimas Visitas</h2>
      <TabelaVisitas
        visitas={ultimas}
        colunas={["responsavel", "aluno", "curso", "inicio", "status"]}
        podeEditar={podeEditar}
        onEditar={acoes.abrirEdicao}
        onFicha={acoes.abrirFicha}
        onExcluir={acoes.excluir}
      />

      {acoes.modais}
    </>
  );
}
